import { Select } from "./ui/select.tsx";
import { useId } from "react";
import { PRODUCT_SOURCE_LABELS as L } from "./labels.ts";
import { Callout, Field } from "./ui.tsx";

export interface ProductSourceForm {
  repository: string;
  repositoryRef: string;
  sourceOfTruth: "website" | "repository";
  websiteStatus: "current" | "previous_version";
}

/** Source controls shared by onboarding and the product profile. */
export function ProductSourceFields({ value, onChange, disabled }: { value: ProductSourceForm; onChange: (value: ProductSourceForm) => void; disabled?: boolean }) {
  const hasRepository = value.repository.trim().length > 0;
  const setRepository = (repository: string) => {
    const added = !hasRepository && repository.trim().length > 0;
    onChange({
      ...value,
      repository,
      repositoryRef: repository.trim() ? value.repositoryRef : "",
      sourceOfTruth: repository.trim() ? (added ? "repository" : value.sourceOfTruth) : "website",
      websiteStatus: repository.trim() ? value.websiteStatus : "current",
    });
  };
  return (
    <>
      <Field label={L.repository} help={L.repositoryHelp}>
        <input className="z-input" aria-label={L.repository} placeholder={L.repositoryPlaceholder} minLength={3} maxLength={300} value={value.repository} disabled={disabled} onChange={(e) => setRepository(e.target.value)} />
      </Field>
      {hasRepository && (
        <>
          <Field label={L.repositoryRef} help={L.repositoryRefHelp}>
            <input className="z-input" aria-label={L.repositoryRef} placeholder={L.repositoryRefPlaceholder} maxLength={200} value={value.repositoryRef} disabled={disabled} onChange={(e) => onChange({ ...value, repositoryRef: e.target.value })} />
          </Field>
          <Field label={L.sourceOfTruth} help={L.sourceOfTruthHelp}>
            <Select variant="form" label={L.sourceOfTruth} value={value.sourceOfTruth} disabled={disabled || value.websiteStatus === "previous_version"} onValueChange={(nextValue) => onChange({ ...value, sourceOfTruth: nextValue as ProductSourceForm["sourceOfTruth"] })} options={[{ value: "website", label: L.websiteSource }, { value: "repository", label: L.repositorySource }]} />
          </Field>
        </>
      )}
      <Field label={L.websiteStatus} help={value.websiteStatus === "previous_version" ? L.previousVersionHelp : undefined}>
        <Select variant="form" label={L.websiteStatus} value={value.websiteStatus} disabled={disabled} onValueChange={(nextValue) => {
          const websiteStatus = nextValue as ProductSourceForm["websiteStatus"];
          onChange({ ...value, websiteStatus, sourceOfTruth: websiteStatus === "previous_version" ? "repository" : value.sourceOfTruth });
        }} options={[{ value: "current", label: L.websiteCurrent }, { value: "previous_version", label: L.websitePrevious, disabled: !hasRepository }]} />
      </Field>
      {value.websiteStatus === "previous_version" && <Callout>{L.previousWebsiteNotice}</Callout>}
    </>
  );
}

export function RepositoryConsent({ checked, onChange, disabled, required = true }: { checked: boolean; onChange: (checked: boolean) => void; disabled?: boolean; required?: boolean }) {
  const id = useId();
  return (
    <ul className="z-check">
      <li>
        <input id={id} type="checkbox" checked={checked} required={required} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        <label htmlFor={id}>{L.consent}</label>
      </li>
    </ul>
  );
}

export function GitHubTokenField({ value, onChange, disabled, required }: { value: string; onChange: (value: string) => void; disabled?: boolean; required?: boolean }) {
  return (
    <Field label={L.token} help={L.tokenHelp}>
      <input className="z-input" type="password" aria-label={L.token} autoComplete="off" spellCheck={false} placeholder={L.tokenPlaceholder} title={L.tokenHelp} pattern="github_pat_[A-Za-z0-9_]{20,}" value={value} disabled={disabled} required={required} minLength={20} maxLength={255} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

export function sourceRequest(value: ProductSourceForm, consent: boolean) {
  return {
    repository: value.repository.trim() || null,
    repository_ref: value.repositoryRef.trim() || null,
    source_of_truth: value.sourceOfTruth,
    website_status: value.websiteStatus,
    ...(value.repository.trim() ? { repository_consent: consent } : {}),
  };
}
